import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, IconButton, Tooltip, MenuItem } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { blue, grey } from "@mui/material/colors";
import "./FuelEuSlippageCoefficient.scss";
import { preventScientificNotation, roundOff } from "../../../../../services/utilities";
import axiosHttp from "../../../../../services/interceptor";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";

const getRowKey = (rowOrGroupId, fuelType, legalFramework) => {
	if (typeof rowOrGroupId === "object") {
		const row = rowOrGroupId;
		return `${row.groupId}_${row.fuelType}_${row.legalFramework}`;
	}
	return `${rowOrGroupId}_${fuelType}_${legalFramework}`;
};

const FuelEuSlippageCoefficient = forwardRef(({ reasonForChanges, refetchMonitoringData,fuelTypes, shouldRefetch,vesselMstId }, ref) => {
	const [tableData, setTableData] = useState([]);
	const [validationErrors, setValidationErrors] = useState({});
	const [showExtraColumns, setShowExtraColumns] = useState(false);
	const [rowBackup, setRowBackup] = useState(null);
	const [editedRowsMap, setEditedRowsMap] = useState({});
	
	const transformB5SlippageData = (apiData, editedRowsMap = {}) => {
		 let transformed =
			apiData?.flatMap((group) => {
				if (!group?.slippageCoefficientPercent || group.slippageCoefficientPercent.length === 0) {
					return [
						{
							emissionSourceReferenceNumber: group.emissionSourceReferenceNumber,
							groupId: group.emissionSourceReferenceNumber,
							b5SlippageCoefficientId: group.b5SlippageCoefficientId,
							factorReference: "",
							fuelType: "",
							legalFramework: "",
							slipPercentage: "",
							reasonForChange: null,
							additionalNote: null,
							isSlipEdited: false,
							isEditable: false,
						},
					];
				}

				return group.slippageCoefficientPercent.map((coeff) => {
					const baseRow = {
						...coeff,
						emissionSourceReferenceNumber: group.emissionSourceReferenceNumber,
						groupId: group.emissionSourceReferenceNumber,
						b5SlippageCoefficientId: group.b5SlippageCoefficientId,
						reasonForChange: coeff?.reasonForChange ?? null,
						additionalNote: coeff?.additionalNote ?? null,
						isSlipEdited: coeff?.factorReference === "Defined by user",
					};

					const rowKey = getRowKey(group.b5SlippageCoefficientId, coeff.fuelType, coeff.legalFramework);
					return baseRow;
				});
			}) || [];
		transformed = transformed.sort((a, b) => {
			if (a.emissionSourceReferenceNumber === b.emissionSourceReferenceNumber) {
				return a.fuelType.localeCompare(b.fuelType);
			}
			return a.emissionSourceReferenceNumber - b.emissionSourceReferenceNumber;
		});
		for (let i = 0; i < transformed.length; i++) {
			const current = transformed[i];
			const previous = transformed[i - 1];
			current.isGroupStart = i === 0 || current.emissionSourceReferenceNumber !== previous?.emissionSourceReferenceNumber;
		}
		return transformed;
	};
	
	const getB5PrefilledData = async () => {
		try {
			const response = await callApi({ url: API_ROUTES.GET_B3_PREFILL_B5(vesselMstId), method: "GET", data: {} });
			if (response) {
				const transformed = transformB5SlippageData(response, editedRowsMap);
				setTableData(transformed);
				const hasReasonForChange = transformed.some((row) => !!row.reasonForChange);
				setShowExtraColumns(hasReasonForChange);
			}
		} catch (error) {
			console.error("Error fetching slippage data", error);
		}
	};

	const [hasFetched, setHasFetched] = useState(false);

	useEffect(() => {
		if (shouldRefetch && !hasFetched) {
			getB5PrefilledData();
			setHasFetched(true);
		}
	}, [shouldRefetch]);

	useEffect(() => {
		if (!shouldRefetch) {
			setHasFetched(false);
		}
	}, [shouldRefetch]);

	const columns = useMemo(() => {
		const cols = [
			{
				accessorKey: "emissionSourceReferenceNumber",
				header: "Fuel Consumer reference No",
				enableEditing: false,
				Cell: ({ cell, row }) => {
					const value = row.original?.emissionSourceReferenceNumber;
					const isGroupStart = row.original?.isGroupStart;
					if (!isGroupStart) return null;
					return (
						<Box>
							<div style={{ fontWeight: "bold" }}>{value}</div>
						</Box>
					);
				},
				muiTableHeadCellProps: {
					sx: { borderBottom: "none", borderRight: "1px solid lightgray" },
				},
				size: 120,
			},
			{
				id: "slip_coeff",
				header: "Slippage coefficient (as % of mass of fuel used by the specific emission source) in accordance with Annex I to Regulation (EU) 2015/757",
				columns: [
					{
						accessorKey: "factorReference",
						header: "Factor Reference",
						enableEditing: false,
					},
					{
						accessorKey: "fuelType",
						header: "Fuel Type",
						enableEditing: false,
						 Cell: ({ cell }) => {
                            const val = cell.getValue();
                            const matched = fuelTypes.find((opt) => opt.code === val);
                            return matched ? matched.name : val;
                        }
					},
					{
						accessorKey: "legalFramework",
						header: "Legal Framework",
						enableEditing: false,
					},
					{
						accessorKey: "slipPercentage",
						header: "Slip Percentage [0..1]",
						Cell: ({ cell }) => {
							const value = cell.getValue();
							return value !== "" && !isNaN(value) ? parseFloat(Number(value).toFixed(4)) : "";
						},
						muiEditTextFieldProps: ({ row, table }) => {
							const isEditable = row.original?.isEditable;
							const rowId = row.id;
							return {
								type: "number",
								inputProps: { min: 0, max: 1, step: 0.0001 },
								disabled: !isEditable,
								error: !!validationErrors[rowId],
								helperText: validationErrors[rowId] || "",
								onChange: (e) => {
									const inputValue = e.target.value;
									const value = parseFloat(inputValue);
									let errorMessage = "";

									if (inputValue === "") errorMessage = "Slip % is required";
									else if (isNaN(value) || value < 0 || value > 1) errorMessage = "Slip % must be between 0 and 1";

									const isValid = errorMessage === "";
									setValidationErrors((prev) => ({ ...prev, [rowId]: errorMessage }));
									table.options.meta?.updateData(row.index, "slipPercentage", inputValue === "" ? "" : value);
									table.options.meta?.updateData(row.index, "isSlipEdited", isValid);
									if (isValid && row.original.isEditable && !showExtraColumns) {
										setShowExtraColumns(true);
									}
								},
								onKeyDown: (e) => {
									preventScientificNotation(e);
									if (e.key === "ArrowUp" || e.key === "ArrowDown") {
										e.preventDefault();
									}
								},
								onWheel: (e) => e.target.blur(),
							};
						},
					},
				],
			},
		];

		if (showExtraColumns) {
			cols[1].columns.push(
				{
					accessorKey: "reasonForChange",
					header: "Reason for Change",
					size: 280,
					muiEditTextFieldProps: ({ row, table }) => ({
						select: true,
						disabled: !row.original.isSlipEdited,
						value: row.original.reasonForChange || "",
						onChange: (e) => {
							const newValue = e.target.value;
							table.options.meta?.updateData(row.index, "reasonForChange", newValue);
							const rowKey = getRowKey(row.original);
							setEditedRowsMap((prev) => ({
								...prev,
								[rowKey]: {
									...prev[rowKey],
									reasonForChange: newValue,
								},
							}));
						},
						error: row.original.isSlipEdited && !row.original.reasonForChange,
						helperText: row.original.isSlipEdited && !row.original.reasonForChange ? "field is required" : "",
						children: [
							<MenuItem key="placeholder" value="">
								Select reason
							</MenuItem>,
							...reasonForChanges.map((option) => (
								<MenuItem key={option.id} value={option.name}>
									{option.name}
								</MenuItem>
							)),
						],
					}),
				},
				{
					accessorKey: "additionalNote",
					header: "Additional Notes",
					muiEditTextFieldProps: ({ row, table }) => ({
						disabled: !row.original.isSlipEdited,
						placeholder: "Optional",
						value: row.original.additionalNote || "",
						inputProps: { maxLength: 1000 },
						onChange: (e) => {
							const inputValue = e.target.value;
							if (inputValue?.length <= 1000) {
								table.options.meta?.updateData(row.index, "additionalNote", inputValue);
								const rowKey = getRowKey(row.original);
								setEditedRowsMap((prev) => ({
									...prev,
									[rowKey]: {
										...prev[rowKey],
										additionalNote: inputValue,
									},
								}));
							}
						},
					}),
				}
			);
		}

		return cols;
	}, [validationErrors, showExtraColumns, editedRowsMap]);

	useImperativeHandle(ref, () => ({
		async submitAndCheck() {
			const { editingRow } = table.getState();
			if (!editingRow) return true;

			const editedValues = editingRow._valuesCache || {};
			const originalValues = editingRow.original || {};

			const isSlipEdited = originalValues.isSlipEdited;
			const reasonForChange = editedValues.reasonForChange ?? originalValues.reasonForChange;

			if (isSlipEdited && (!reasonForChange || reasonForChange.trim() === '')) {
				setValidationErrors((prev) => ({
					...prev,
					reasonForChange: "Reason for change is required when slip is edited.",
				}));
				return false;
			}

			if (isSlipEdited) {
				await handleSaveSlipCoefficient({ row: editingRow, values: editedValues }, true);
				return true;
			} else {
				table.setEditingRow(null);
				return true;
			}
		}
	}));

	const handleSaveSlipCoefficient = async ({ row }, isAuto = false) => {
		const updatedRow = row.original;
		if (updatedRow.isSlipEdited && !updatedRow.reasonForChange) return;

		const groupedData = {};
		for (const row of tableData) {
			const {
				groupId,
				b5SlippageCoefficientId,
				factorReference,
				fuelType,
				legalFramework,
				slipPercentage,
				isSlipEdited,
				reasonForChange,
				additionalNote,
				isEditable,
			} = row;

			if (isSlipEdited && !reasonForChange) return;

			if (!groupedData[groupId]) {
				groupedData[groupId] = {
					b5SlippageCoefficientId,
					emissionSourceReferenceNumber: groupId,
					slippageCoefficientPercent: [],
				};
			}

			const isActualRow = factorReference || fuelType || legalFramework || slipPercentage !== "" || isSlipEdited;
			if (isActualRow) {
				groupedData[groupId].slippageCoefficientPercent.push({
					factorReference: isSlipEdited ? "Defined by user" : factorReference,
					fuelType,
					legalFramework,
					slipPercentage,
					isEditable,
					reasonForChange: reasonForChange ?? null,
					additionalNote: additionalNote ?? null,
				});
			}
		}

		const finalPayload = Object.values(groupedData);
		try {
			const response = await callApi({
				method: "POST",
				url: API_ROUTES.B32(vesselMstId),
				data: finalPayload,
			});

			if (response?.isSuccess) {
				emitToasterMsg("success", "Table Slippage Coefficient", isAuto ? "Slip coefficient has been autosaved successfully" : "Slip coefficient has been updated successfully");
				table.setEditingRow(null);
				const rowKey = getRowKey(updatedRow);
				const rowsMap = {
					...editedRowsMap,
					[rowKey]: {
						reasonForChange: updatedRow.reasonForChange,
						additionalNote: updatedRow.additionalNote,
					},
				};
				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
				await getB5PrefilledData();
				const updatedTableData = transformB5SlippageData(finalPayload, rowsMap);
				setTableData(updatedTableData);
			} else {
				emitToasterMsg("error", "Table Slippage Coefficient", response?.errorMessage || "Save failed");
			}
		} catch (error) {
			emitToasterMsg("error", "Table Slippage Coefficient", error.errorMessage);
		}
	};

	const handleFuelActions = (table, row) => {
		setRowBackup({ ...row.original });
		table.setEditingRow(row);
	};

	const table = useMaterialReactTable({
		columns,
		data: tableData || [],
		enableStickyHeader: true,
		enableSorting: false,
		enableRowSelection: false,
		enableTopToolbar: false,
		enableBottomToolbar: false,
		enableColumnActions: false,
		editDisplayMode: "row",
		enableEditing: true,
		enablePagination: false,
		onEditingRowSave: handleSaveSlipCoefficient,
		onEditingRowCancel: ({ row }) => {
			if (rowBackup) {
				setTableData((prev) => prev.map((r, i) => (i === row.index ? rowBackup : r)));
				setRowBackup(null);
			}
			setValidationErrors({});
			const hasReasonForChange = tableData.some((row) => typeof row.reasonForChange === "string" && row.reasonForChange.trim() !== "");
			setShowExtraColumns(hasReasonForChange);
		},
		muiTableHeadCellProps: {
			sx: {
				color: "white",
				backgroundColor: "#042a4c",
				borderRight: "1px solid lightgray",
				textAlign: "center",
			},
		},
		muiTablePaperProps: { sx: { boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)" } },
		displayColumnDefOptions: {
			"mrt-row-actions": {
				header: "Actions",
				size: 90,
				muiTableHeadCellProps: {
					sx: { borderBottom: "none", borderRight: "1px solid lightgray" },
				},
				muiTableBodyCellProps: {
					sx: {
						borderRight: "1px solid lightgray",
						padding: "4px",
					},
				},
			},
		},
		meta: {
			updateData: (rowIndex, columnId, value) => {
				setTableData((prevData) =>
					prevData.map((row, index) => {
						if (index !== rowIndex) return row;
						const updatedRow = { ...row, [columnId]: value };
						if (columnId === "slipPercentage" && row.isEditable && value !== "" && !isNaN(value)) {
							updatedRow.isSlipEdited = true;
							updatedRow.factorReference = "Defined by user";
							updatedRow.slipPercentage = roundOff(value, 4, 4);
						}
						return updatedRow;
					})
				);
			},
		},
		muiTableContainerProps: { sx: { maxHeight: "350px", overflowY: "auto" } },
		muiTableBodyCellProps: {
			sx: {
				borderRight: "1px solid lightgray",
				"&:last-child": { borderRight: "none" },
			},
		},
		muiTableBodyRowProps: ({ row }) => {
			const currentGroup = row.original?.groupId;
			const previousGroup = row.index > 0 ? tableData[row.index - 1]?.groupId : null;
		  	const isGroupStart = row.index === 0 || currentGroup !== previousGroup;
			const isGroupEnd = row.index === tableData?.length - 1;
			return {
				sx: {
					"& td": { fontSize: "0.875rem" },
					"& td:nth-of-type(2)": {
							borderTop: isGroupStart ? "1px solid #ccc" : "none",
						borderBottom: isGroupEnd ? "1px solid #ccc" : "none",
					},
				},
			};
		},
		renderRowActions: ({ row, table }) => (
			<Box sx={{ display: "flex" }}>
				<Tooltip title="Edit">
					<IconButton
						style={{ padding: "0px" }}
						onClick={row.original?.isEditable ? () => handleFuelActions(table, row) : undefined}
						disabled={!row.original?.isEditable}
					>
						<EditIcon sx={{ color: row.original?.isEditable ? blue[900] : grey[500] }} />
					</IconButton>
				</Tooltip>
			</Box>
		),
	});

	return (
		<div className="slip-coeff-table mt-2">
			<MaterialReactTable table={table} />
		</div>
	);
});

export default FuelEuSlippageCoefficient;
